/**
 * Custom hook for managing agent state (model selection and settings).
 * Keeps persisted agent state authoritative without clobbering it on mount.
 */
import { useEffect, useRef, useState } from "react";
import {
  getDefaultModel,
  getModelConfig,
  normalizeSelectedModelId
} from "@/shared/models";
import type { ModelSelectionState } from "@/shared/models";

type AgentStateSnapshot = Partial<ModelSelectionState> | undefined;

interface UseAgentStateOptions {
  agent: {
    identified?: boolean;
    setState?: (state: unknown) => void;
    state?: Partial<ModelSelectionState>;
  };
}

interface LocalAgentPreferences {
  selectedModelId: string;
}

export function resolveAgentPreferences(
  agentState: AgentStateSnapshot
): LocalAgentPreferences {
  const defaultModelId = getDefaultModel().id;
  const selectedModelId =
    agentState?.selectedModelId && getModelConfig(agentState.selectedModelId)
      ? normalizeSelectedModelId(agentState.selectedModelId)
      : defaultModelId;

  return {
    selectedModelId
  };
}

export function buildAgentPreferenceState(
  currentAgentState: AgentStateSnapshot,
  preferences: LocalAgentPreferences
): Partial<ModelSelectionState> {
  return {
    ...(currentAgentState ?? {}),
    selectedModelId: normalizeSelectedModelId(preferences.selectedModelId)
  };
}

export function shouldSyncAgentPreferences(
  currentAgentState: AgentStateSnapshot,
  preferences: LocalAgentPreferences
): boolean {
  return currentAgentState?.selectedModelId !== preferences.selectedModelId;
}

export function useAgentState({ agent }: UseAgentStateOptions) {
  const initialPreferences = resolveAgentPreferences(agent.state);

  const [model, setModelInternal] = useState<string>(
    initialPreferences.selectedModelId
  );

  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!agent.identified) {
      return;
    }

    const remotePreferences = resolveAgentPreferences(agent.state);

    setModelInternal(remotePreferences.selectedModelId);
    hasHydratedRef.current = true;
  }, [agent.identified, agent.state]);

  useEffect(() => {
    if (
      !agent.identified ||
      !hasHydratedRef.current ||
      typeof agent.setState !== "function" ||
      !shouldSyncAgentPreferences(agent.state, {
        selectedModelId: model
      })
    ) {
      return;
    }

    agent.setState(
      buildAgentPreferenceState(agent.state, {
        selectedModelId: model
      })
    );
  }, [agent, agent.identified, model]);

  return {
    model,
    setModel: setModelInternal
  };
}
